import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface FormData {
  step: number;
  startDate: string;
  biddingDuration: string;
  personalInfo: {
    name: string;
    title: string;
    firstName: string;
    surname: string;
    gender: string;
    email: string;
    countryCode: string;
    phone: string;
    dateOfBirth: string;
    address: string;
    region: string;
  };
  driverInfo: {
    maritalStatus: string;
    isHomeOwner: string;
    hasAccidents: string;
    accidents: any[];
    hasConvictions: string;
    convictions: any[];
    livedInUKSinceBirth: string;
    employmentStatus: string;
    licenseType: string;
    licenseHeld: {
      years: string;
      months: string;
    };
    otherVehicles: string;
  };
  vehicleInfo: {
    make: string;
    model: string;
    ownership: {
      isOwner: string;
      isKeeper: string;
      purchaseDate: string;
      notPurchased: boolean;
      parkingLocation: string;
    };
    transmission: string;
    drivePosition: string;
    seats: string;
    alarm: string;
    tracker: string;
    year: string;
    value: string;
    registration: string;
    mileage: string;
    isModified: string;
    modifications: { type_id: string, details: string }[];
  };
  coverageInfo: {
    noClaimsYears: string;
    coverageType: string;
    protectNoClaims: boolean;
    additionalCover: string[];
    paymentFrequency: string;
    hadInsuranceBefore: string;
  };
}

export function useQuoteForm(user: any) {
  const [formData, setFormData] = useState<FormData>({
    step: 1,
    startDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    biddingDuration: '24',
    personalInfo: { 
      name: '', 
      title: '',
      firstName: '',
      surname: '',
      gender: '', 
      email: '', 
      countryCode: '+44', 
      phone: '', 
      dateOfBirth: '', 
      address: '',
      region: ''
    },
    driverInfo: {
      maritalStatus: '',
      isHomeOwner: '',
      livedInUKSinceBirth: '',
      hasAccidents: '',
      accidents: [],
      hasConvictions: '',
      convictions: [],
      employmentStatus: '',
      licenseType: '',
      licenseHeld: {
        years: '',
        months: ''
      },
      otherVehicles: ''
    },
    vehicleInfo: { 
      make: '', model: '', year: '', transmission: 'Manual', drivePosition: 'Right',
      seats: '5', alarm: 'Standard Factory Fit', tracker: 'No', value: '', registration: '', mileage: '', 
      isModified: 'No', modifications: [],
      ownership: {
        isOwner: '',
        isKeeper: '',
        purchaseDate: '',
        notPurchased: false,
        parkingLocation: ''
      }
    },
    coverageInfo: { 
      noClaimsYears: '', 
      coverageType: '', 
      protectNoClaims: false,
      additionalCover: [],
      paymentFrequency: '',
      hadInsuranceBefore: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModifications, setShowModifications] = useState(false);

  // Pre-fill form with user data when available
  useEffect(() => {
    if (user && user.user_metadata) {
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          name: user.user_metadata.name || '',
          email: user.email || '',
          phone: user.user_metadata.phone?.replace(/^\+\d+/, '') || '',
          countryCode: user.user_metadata.phone?.match(/^\+\d+/)?.[0] || '+44'
        }
      }));
    }
  }, [user]);

  const handleInputChange = (
    section: keyof FormData,
    field: string,
    value: string | string[] | boolean | { [key: string]: any }
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section as keyof FormData],
        [field]: typeof value === 'object' && !Array.isArray(value)
          ? { ...prev[section as keyof FormData][field], ...value }
          : value,
      },
    }));
  };

  const nextStep = () => setFormData(prev => ({ ...prev, step: prev.step + 1 }));
  const prevStep = () => setFormData(prev => ({ ...prev, step: prev.step - 1 }));

  const handleModificationsComplete = (modifications: { type_id: string, details: string }[]) => {
    setFormData(prev => ({
      ...prev,
      vehicleInfo: { 
        ...prev.vehicleInfo, 
        modifications 
      }
    }));
    setShowModifications(false);
    nextStep();
  };

  return {
    formData,
    setFormData,
    isSubmitting,
    setIsSubmitting,
    error,
    setError,
    showModifications,
    setShowModifications,
    handleInputChange,
    nextStep,
    prevStep,
    handleModificationsComplete
  };
}